<script>
  import { createEventDispatcher, onMount } from 'svelte'
  import { getWorkouts, deleteWorkout, getExercisesByWorkoutId, migrateExerciseFormat } from '../services/db.js'
  import { createTemplateFromWorkout } from '../services/templates.js'
  import { format } from 'date-fns'

  const dispatch = createEventDispatcher()

  let workouts = []
  let filteredWorkouts = []
  let isLoading = true
  let filters = {
    type: null,
    startDate: '',
    endDate: ''
  }
  let expandedWorkouts = {}
  let showSaveAsTemplateDialog = false
  let saveAsTemplateWorkoutId = null
  let templateName = ''
  let templateDescription = ''
  let currentPage = 1
  let itemsPerPage = 10

  const workoutTypes = [
    { value: 'strength', label: '💪 重訓' },
    { value: 'cardio', label: '🏃 有氧' },
    { value: 'flexibility', label: '🧘 伸展' },
    { value: 'sports', label: '⚽ 運動' },
    { value: 'other', label: '📋 其他' }
  ]

  onMount(async () => {
    await loadWorkouts()
  })

  async function loadWorkouts() {
    isLoading = true
    try {
      workouts = await getWorkouts(filters)
      filteredWorkouts = [...workouts]
    } catch (error) {
      console.error('Failed to load workouts:', error)
      alert('載入訓練記錄失敗')
    } finally {
      isLoading = false
    }
  }

  function setTypeFilter(type) {
    filters.type = type
  }

  async function applyFilters() {
    currentPage = 1
    await loadWorkouts()
  }

  async function clearFilters() {
    filters = { type: null, startDate: '', endDate: '' }
    currentPage = 1
    await loadWorkouts()
  }

  function goToPage(page) {
    currentPage = page
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function nextPage() {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1)
    }
  }

  function prevPage() {
    if (currentPage > 1) {
      goToPage(currentPage - 1)
    }
  }

  function getTypeEmoji(type) {
    const emojis = { strength: '💪', cardio: '🏃', flexibility: '🧘', sports: '⚽', other: '📋' }
    return emojis[type] || '📋'
  }

  function getTypeName(type) {
    const names = { strength: '重量訓練', cardio: '有氧運動', flexibility: '伸展/瑜珈', sports: '運動競技', other: '其他' }
    return names[type] || '其他'
  }

  function handleEdit(workoutId) {
    dispatch('edit', { workoutId })
  }

  async function handleDelete(workout) {
    const date = new Date(workout.date)
    const typeName = getTypeName(workout.type)
    const dateStr = format(date, 'yyyy/MM/dd HH:mm')

    if (confirm(`確定要刪除這筆訓練記錄嗎？\n\n${typeName} - ${dateStr}\n\n此操作無法復原。`)) {
      try {
        await deleteWorkout(workout.id)
        showToast('訓練記錄已刪除', 'success')
        await loadWorkouts()
        dispatch('delete', { workoutId: workout.id })
      } catch (error) {
        console.error('Delete failed:', error)
        showToast('刪除失敗，請稍後再試', 'error')
      }
    }
  }

  async function toggleExercises(workoutId) {
    if (expandedWorkouts[workoutId]) {
      expandedWorkouts[workoutId] = null
    } else {
      try {
        const exercises = await getExercisesByWorkoutId(workoutId)
        // Migrate to new format for display
        expandedWorkouts[workoutId] = exercises.map(migrateExerciseFormat)
      } catch (error) {
        console.error('Failed to load exercises:', error)
      }
    }
    expandedWorkouts = expandedWorkouts
  }

  function formatSetsDisplay(exercise) {
    if (!Array.isArray(exercise.sets)) {
      // Old format fallback
      return `${exercise.sets || 0} 組 × ${exercise.reps || 0} 次${exercise.weight ? ` @ ${exercise.weight} kg` : ''}`
    }

    const totalSets = exercise.sets.length
    const completedSets = exercise.sets.filter(s => s.completed).length

    // Check if all sets have same weight and reps
    const allSame = exercise.sets.every(s =>
      s.weight === exercise.sets[0].weight && s.reps === exercise.sets[0].reps
    )

    if (allSame && totalSets > 0) {
      const set = exercise.sets[0]
      return `${totalSets} 組 × ${set.reps} 次${set.weight ? ` @ ${set.weight} kg` : ''} (${completedSets}/${totalSets} 完成)`
    }

    // Show individual sets
    return `${completedSets}/${totalSets} 組完成`
  }

  function handleSaveAsTemplate(workout) {
    saveAsTemplateWorkoutId = workout.id
    templateName = `${getTypeName(workout.type)} - ${format(new Date(workout.date), 'MM/dd')}`
    templateDescription = ''
    showSaveAsTemplateDialog = true
  }

  async function handleConfirmSaveAsTemplate() {
    if (!templateName.trim()) {
      alert('請輸入模板名稱')
      return
    }

    try {
      await createTemplateFromWorkout(saveAsTemplateWorkoutId, templateName.trim(), templateDescription.trim())
      showToast('已儲存為模板', 'success')
      handleCloseSaveAsTemplateDialog()
    } catch (error) {
      console.error('Failed to save as template:', error)
      showToast('儲存模板失敗，請稍後再試', 'error')
    }
  }

  function handleCloseSaveAsTemplateDialog() {
    showSaveAsTemplateDialog = false
    saveAsTemplateWorkoutId = null
    templateName = ''
    templateDescription = ''
  }

  function showToast(message, type = 'success') {
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500'
    const toast = document.createElement('div')
    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in`
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 2000)
  }

  $: hasFilters = filters.type || filters.startDate || filters.endDate
  $: totalPages = Math.ceil(filteredWorkouts.length / itemsPerPage)
  $: paginatedWorkouts = filteredWorkouts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  $: pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
</script>

<div class="workout-list-container fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
  <div class="max-w-4xl mx-auto px-4 py-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">訓練記錄</h1>
        <p class="text-sm text-gray-600 mt-1">
          共 {filteredWorkouts.length} 筆記錄
          {#if totalPages > 1}
            <span class="text-gray-400">• 第 {currentPage} / {totalPages} 頁</span>
          {/if}
        </p>
      </div>
      <button
        type="button"
        class="text-gray-600 hover:text-gray-800 text-2xl font-bold"
        on:click={() => dispatch('close')}
      >
        ✕
      </button>
    </div>

    <!-- Filters -->
    <div class="card mb-6">
      <h2 class="text-lg font-semibold mb-4 text-gray-800">篩選條件</h2>

      <div class="space-y-4">
        <!-- Type Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">訓練類型</label>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="filter-type-btn"
              class:active={filters.type === null}
              on:click={() => setTypeFilter(null)}
            >
              全部
            </button>
            {#each workoutTypes as type}
              <button
                type="button"
                class="filter-type-btn"
                class:active={filters.type === type.value}
                on:click={() => setTypeFilter(type.value)}
              >
                {type.label}
              </button>
            {/each}
          </div>
        </div>

        <!-- Date Range -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">開始日期</label>
            <input type="date" class="input-field w-full" bind:value={filters.startDate} />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">結束日期</label>
            <input type="date" class="input-field w-full" bind:value={filters.endDate} />
          </div>
        </div>

        <!-- Filter Actions -->
        <div class="flex gap-2">
          <button type="button" class="btn btn-primary" on:click={applyFilters}>套用篩選</button>
          <button type="button" class="btn btn-secondary" on:click={clearFilters}>清除篩選</button>
        </div>
      </div>
    </div>

    <!-- Workout List -->
    <div class="space-y-4">
      {#if isLoading}
        <div class="text-center py-12">
          <div class="spinner mx-auto mb-4"></div>
          <p class="text-gray-600">載入中...</p>
        </div>
      {:else if filteredWorkouts.length === 0}
        <div class="card text-center py-12">
          {#if hasFilters}
            <div class="text-6xl mb-4">🔍</div>
            <h3 class="text-xl font-semibold text-gray-800 mb-2">找不到符合條件的記錄</h3>
            <p class="text-gray-600 mb-4">試試調整篩選條件</p>
            <button type="button" class="btn btn-secondary" on:click={clearFilters}>清除篩選</button>
          {:else}
            <div class="text-6xl mb-4">📝</div>
            <h3 class="text-xl font-semibold text-gray-800 mb-2">尚無訓練記錄</h3>
            <p class="text-gray-600">開始記錄你的第一次訓練吧！</p>
          {/if}
        </div>
      {:else}
        {#each paginatedWorkouts as workout (workout.id)}
          {@const date = new Date(workout.date)}
          <div class="card hover:shadow-lg transition-shadow workout-card">
            <div class="flex justify-between items-start mb-3">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-3xl">{getTypeEmoji(workout.type)}</span>
                  <div>
                    <div class="font-semibold text-gray-800 text-lg">{getTypeName(workout.type)}</div>
                    <div class="text-sm text-gray-500">{format(date, 'yyyy/MM/dd')} {format(date, 'HH:mm')}</div>
                  </div>
                  {#if !workout.synced}
                    <span class="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">待同步</span>
                  {/if}
                </div>
              </div>
              <div class="flex gap-2">
                <button
                  type="button"
                  class="text-green-600 hover:text-green-800 text-sm font-medium"
                  on:click={() => handleSaveAsTemplate(workout)}
                  title="儲存為模板"
                >
                  📋
                </button>
                <button
                  type="button"
                  class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  on:click={() => handleEdit(workout.id)}
                >
                  編輯
                </button>
                <button
                  type="button"
                  class="text-red-600 hover:text-red-800 text-sm font-medium"
                  on:click={() => handleDelete(workout)}
                >
                  刪除
                </button>
              </div>
            </div>

            {#if workout.duration}
              <div class="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <span>⏱️</span>
                <span>{Math.floor(workout.duration / 60)} 分鐘</span>
              </div>
            {/if}

            {#if workout.notes}
              <div class="text-sm text-gray-600 mb-3 bg-gray-50 p-3 rounded">
                {workout.notes}
              </div>
            {/if}

            <button
              type="button"
              class="text-sm text-gray-500 cursor-pointer hover:text-gray-700"
              on:click={() => toggleExercises(workout.id)}
            >
              {expandedWorkouts[workout.id] ? '隱藏運動項目 ↑' : '點擊查看運動項目 →'}
            </button>

            {#if expandedWorkouts[workout.id]}
              <div class="exercises-list mt-3 bg-gray-50 rounded-lg p-3">
                <div class="text-sm font-medium text-gray-700 mb-2">運動項目：</div>
                {#if expandedWorkouts[workout.id].length === 0}
                  <div class="text-sm text-gray-500">此次訓練無運動項目記錄</div>
                {:else}
                  <div class="space-y-2">
                    {#each expandedWorkouts[workout.id] as ex, index}
                      <div class="bg-white p-2 rounded text-xs">
                        <div class="font-medium text-gray-800">{index + 1}. {ex.exercise_name}</div>
                        <div class="text-gray-600 mt-1">
                          {formatSetsDisplay(ex)}
                          {ex.rest_time ? ` • 休息 ${ex.rest_time}秒` : ''}
                        </div>
                        {#if Array.isArray(ex.sets) && ex.sets.length > 0}
                          <div class="mt-2 space-y-1">
                            {#each ex.sets as set, setIndex}
                              <div class="flex items-center gap-2 text-xs" class:text-green-600={set.completed} class:text-gray-400={!set.completed}>
                                <span class="w-4">{setIndex + 1}.</span>
                                <span>{set.weight || 0} kg × {set.reps || 0} 次</span>
                                {#if set.completed}
                                  <span>✓</span>
                                {/if}
                              </div>
                            {/each}
                          </div>
                        {/if}
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        {/each}

        <!-- Pagination -->
        {#if totalPages > 1}
          <div class="card mt-6">
            <div class="flex items-center justify-between">
              <!-- Previous Button -->
              <button
                type="button"
                class="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage === 1}
                on:click={prevPage}
              >
                ← 上一頁
              </button>

              <!-- Page Numbers -->
              <div class="flex gap-2 flex-wrap justify-center">
                {#each pageNumbers as page}
                  {#if totalPages <= 7 || page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)}
                    <button
                      type="button"
                      class="px-3 py-1 rounded text-sm font-medium transition-colors {page === currentPage ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}"
                      on:click={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  {:else if page === currentPage - 2 || page === currentPage + 2}
                    <span class="px-2 text-gray-400">...</span>
                  {/if}
                {/each}
              </div>

              <!-- Next Button -->
              <button
                type="button"
                class="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage === totalPages}
                on:click={nextPage}
              >
                下一頁 →
              </button>
            </div>

            <!-- Page Info -->
            <div class="text-center text-sm text-gray-500 mt-3">
              顯示第 {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredWorkouts.length)} 筆，共 {filteredWorkouts.length} 筆
            </div>
          </div>
        {/if}
      {/if}
    </div>
  </div>
</div>

<!-- Save as Template Dialog -->
{#if showSaveAsTemplateDialog}
  <div class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <!-- Header -->
      <div class="border-b border-gray-200 p-4">
        <h2 class="text-xl font-semibold text-gray-800">儲存為訓練模板</h2>
      </div>

      <!-- Form -->
      <div class="p-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            模板名稱 <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            class="input-field w-full"
            placeholder="例如：胸背訓練、腿部訓練..."
            bind:value={templateName}
            maxlength="100"
            required
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            描述
          </label>
          <textarea
            class="input-field w-full"
            rows="3"
            placeholder="模板說明、適用時機等..."
            maxlength="500"
            bind:value={templateDescription}
          ></textarea>
        </div>
      </div>

      <!-- Actions -->
      <div class="border-t border-gray-200 p-4 flex gap-3">
        <button
          type="button"
          class="btn btn-secondary flex-1"
          on:click={handleCloseSaveAsTemplateDialog}
        >
          取消
        </button>
        <button
          type="button"
          class="btn btn-primary flex-1"
          on:click={handleConfirmSaveAsTemplate}
        >
          儲存
        </button>
      </div>
    </div>
  </div>
{/if}
